import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { ZeosApiContext } from "../context/ZeosApiContext";
import { IUtilityIntelligenceProfile } from "../services/zeos-api/contracts/UtilityIntelligence";

const isSuccess = (status?: string) => status?.toLowerCase() === "success";

export interface IUtilityProfileState {
  /** Null until loaded, or when nothing has been looked up for the property yet. */
  profile: IUtilityIntelligenceProfile | null;
  loading: boolean;
  error: string;
  /** Re-reads the stored profile, e.g. after a Re-check POST. */
  refreshProfile: () => Promise<void>;
}

/**
 * The stored utility profile (GET v2/territory/{id}) for a saved property. PropertyForm owns one
 * copy and hands it to the panel and the selectors so a Re-check updates both (3898).
 * Pass enabled=false when the parent already supplies the profile; no request is made.
 */
export function useUtilityProfile(propertyId: number, enabled = true): IUtilityProfileState {
  const api = useContext(ZeosApiContext);
  const [profile, setProfile] = useState<IUtilityIntelligenceProfile | null>(null);
  const [loading, setLoading] = useState(enabled && propertyId > 0);
  const [error, setError] = useState("");
  // Drops a slow response for a property the form has since moved off.
  const requestIdRef = useRef(0);

  const refreshProfile = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!enabled || propertyId <= 0 || !api.Territory?.getProfileAsync) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await api.Territory.getProfileAsync(propertyId);
      if (requestId !== requestIdRef.current) return;
      if (response.data || isSuccess(response.zeosStatus)) {
        // Success with no data means nothing has been looked up yet: empty, not an error.
        setProfile(response.data ?? null);
      } else {
        setError(response.message || "Could not load the utility details.");
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError("Could not load the utility details.");
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [api, enabled, propertyId]);

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  return { profile, loading, error, refreshProfile };
}
